import React from 'react';
import {
  PersonOutline,
  LabelOutlined,
  InfoOutlined,
  StarBorderOutlined,
  InsertDriveFileOutlined,
  CalendarTodayOutlined,
  UpdateOutlined,
  OpenInNew
} from '@mui/icons-material';
import {
  getStatusInfo,
  getPriorityInfo,
  formatDate,
  formatDateTime,
  formatDateTimeWithTimezone,
  getInitials,
  safeGet
} from './utils/dataMapping';
import AttachmentCard from './components/AttachmentCard';

const DetailsTicket = ({ ticket, baseUrl }) => {
  console.log('DetailsTicket received ticket:', ticket);

  if (!ticket) {
    return (
      <div className="w-full bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  // Extract ticket data with safe fallbacks - mapped for API response structure
  const ticketNumber = `TKT-${String(ticket.ticket_id || ticket.id || '0000').padStart(4, "0")}`;
  const status = getStatusInfo(ticket.status);
  const priority = getPriorityInfo(ticket.priority || ticket.ticket_priority_id);
  const requesterName = ticket.requester?.name || ticket.requester_name || ticket.creator?.name || 'Unknown';
  const requesterCompany = ticket.requester?.company_name || ticket.company_name || 'Unknown Company';
  const requesterImage = ticket.requester?.image || ticket.creator?.image;
  const requesterInitials = getInitials(requesterName);
  const agentName = ticket.agent?.name || ticket.assing_agent_name || ticket.assign_group || 'Unassigned';
  const agentImage = ticket.agent?.image || ticket.assing_agent_image;
  const agentInitials = getInitials(agentName);
  const typeName = ticket.type || ticket.type_name || ticket.type?.type || 'General';
  const createdAt = ticket.created_at ? formatDate(ticket.created_at) : 'N/A';
  const updatedAt = ticket.updated_at ? formatDateTime(ticket.updated_at) : 'N/A';
  const description = ticket.description || 'No description provided';

  // Parse tags - API returns comma-separated string or array
  const parseTags = () => {
    if (!ticket.tags) return [];
    if (Array.isArray(ticket.tags)) return ticket.tags;
    return ticket.tags.split(',').map(t => t.trim()).filter(Boolean);
  };
  const tags = parseTags();

  // Separate tripid tags from others
  const tripIdTags = tags.filter(t => /^tripid\s*:\s*\d+/i.test(t));
  const otherTags = tags.filter(t => !/^tripid\s*:\s*\d+/i.test(t));

  const openTripDetails = (tag) => {
    const match = tag.match(/^tripid\s*:\s*(\d+)/i);
    if (match) {
      window.open(`/trip_details/${match[1]}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle attachments - API returns single file or array
  let attachments = [];
  if (ticket.file) {
    // If file is an object with path/name
    if (typeof ticket.file === 'object') {
      attachments = [ticket.file];
    }
    // If file is a string path
    else if (typeof ticket.file === 'string') {
      attachments = [{ file_path: ticket.file, name: ticket.file.split('/').pop() }];
    }
  }
  // Fallback to files array if present
  if (!attachments.length && ticket.files && ticket.files.length > 0) {
    attachments = ticket.files;
  }

  return (
    <div className="flex flex-col gap-3 font-ubuntu w-full px-4 border-box">
      {/* Ticket Number Display */}
      <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
        <h2 className="text-gray-900 text-lg font-bold">{ticketNumber}</h2>
      </div>

      {/* Top 3 Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Requester */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-blue-100 text-blue-500 p-2 rounded-md">
            {requesterImage ? (
              <img
                src={`${baseUrl}${requesterImage}`}
                alt={requesterName}
                className="w-6 h-6 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="text-sm">${requesterInitials}</span>`;
                }}
              />
            ) : (
              <PersonOutline fontSize="small" />
            )}
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Requester</p>
            <p className="text-gray-800 font-bold text-sm">{requesterName}</p>
            {requesterCompany && (
              <p className="text-gray-500 text-xs">{requesterCompany}</p>
            )}
          </div>
        </div>

        {/* Agent */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-orange-100 text-orange-500 p-2 rounded-md">
            {agentImage ? (
              <img
                src={`${baseUrl}${agentImage}`}
                alt={agentName}
                className="w-6 h-6 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="text-sm">${agentInitials}</span>`;
                }}
              />
            ) : (
              <LabelOutlined fontSize="small" />
            )}
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Agent</p>
            <p className="text-gray-800 font-bold text-sm">{agentName}</p>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className={`${status.color} text-white p-2 rounded-md`}>
            <InfoOutlined fontSize="small" />
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Status</p>
            <p className="text-gray-800 font-bold text-sm">{status.label}</p>
          </div>
        </div>
      </div>

      {/* Middle 3 Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Priority */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-red-50 text-red-500 p-2 rounded-md">
            <StarBorderOutlined fontSize="small" />
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Priority</p>
            <p className={`text-gray-800 font-bold text-sm uppercase ${priority.color}`}>
              {priority.label}
            </p>
          </div>
        </div>

        {/* Type */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-green-50 text-green-500 p-2 rounded-md">
            <InsertDriveFileOutlined fontSize="small" />
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Type</p>
            <p className="text-gray-800 font-bold text-sm">{typeName}</p>
          </div>
        </div>

        {/* Created */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-indigo-50 text-indigo-500 p-2 rounded-md">
            <CalendarTodayOutlined fontSize="small" />
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Created</p>
            <p className="text-gray-800 font-bold text-sm">{formatDateTimeWithTimezone(ticket.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Bottom 1 Card Row (Last Updated) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Last Updated */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex items-start gap-3 w-full">
          <div className="bg-purple-50 text-purple-500 p-2 rounded-md">
            <UpdateOutlined fontSize="small" />
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Last Updated</p>
            <p className="text-gray-800 font-bold text-sm">{formatDateTimeWithTimezone(ticket.updated_at)}</p>
          </div>
        </div>
      </div>

      {/* Tags Section */}
      {tags.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 w-full">
          <h3 className="text-gray-800 font-bold text-sm mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {tripIdTags.map((tag, i) => {
              const tripId = tag.match(/^tripid\s*:\s*(\d+)/i)?.[1];
              return (
                <button
                  key={`tripid-${i}`}
                  onClick={() => openTripDetails(tag)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer border border-blue-200"
                  title={`View trip #${tripId} details`}
                >
                  <span>Trip #{tripId}</span>
                  <OpenInNew sx={{ fontSize: 14 }} />
                </button>
              );
            })}
            {otherTags.map((tag, i) => (
              <span
                key={`tag-${i}`}
                className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description & Attachments Box */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 w-full mt-1">
        {/* User Badge inside description area */}
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gray-200 text-gray-500 font-bold flex items-center justify-center rounded-full w-10 h-10">
            {requesterImage ? (
              <img
                src={`${baseUrl}${requesterImage}`}
                alt={requesterName}
                className="w-full h-full rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerText = requesterInitials;
                }}
              />
            ) : (
              requesterInitials
            )}
          </div>
          <div>
            <p className="text-gray-800 font-bold text-sm leading-tight">{requesterName}</p>
            {requesterCompany && (
              <p className="text-gray-400 text-xs">{requesterCompany}</p>
            )}
          </div>
        </div>

        {/* Description Text */}
        <div className="mb-8">
          <h3 className="text-gray-800 font-bold text-sm mb-2">Description</h3>
          <p className="text-gray-500 text-sm whitespace-pre-wrap">
            {description}
          </p>
        </div>

        {/* Attachments Section */}
        {attachments.length > 0 && (
          <div>
            <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-4">
              <h3 className="text-gray-800 font-bold text-sm">Attachments</h3>
              <span className="text-gray-400 text-xs">{attachments.length} file(s)</span>
            </div>

            <div className="flex flex-col gap-3">
              {attachments.map((file, index) => (
                <AttachmentCard
                  key={`${file.id || index}-${file.name || file.file_name}`}
                  file={file}
                  baseUrl={baseUrl}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsTicket;
